import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, TextField } from "@material-ui/core";
import useFirestore from "../../../hooks/useFirestore";
import type { AdditionalUser } from "../../../@types/listings/user";

type OrgUserOption = { label: string; value: string };

type CommonProps = {
  label?: string;
  size?: "small" | "medium";
  fullWidth?: boolean;
  /** Additional sx for the Autocomplete root. */
  sx?: object;
};

type SingleProps = CommonProps & {
  multiple?: false;
  /** Selected user email, or null if none. */
  value: string | null;
  onChange: (value: string | null) => void;
};

type MultiProps = CommonProps & {
  multiple: true;
  /** Selected user emails. */
  value: string[];
  onChange: (value: string[]) => void;
};

type OrgUsersPickerProps = SingleProps | MultiProps;

/**
 * Picker for the org's users. Reads `organizationUsers` from FirestoreContext
 * and renders an Autocomplete keyed by email (the user document id). Supports
 * single or multi-select via the `multiple` prop. The caller is expected to
 * gate visibility (e.g. only show to org admins) — this component renders
 * nothing when the org has no other users.
 */
export default function OrgUsersPicker(props: OrgUsersPickerProps) {
  const { t } = useTranslation();
  const { organizationUsers } = useFirestore();

  const options: OrgUserOption[] = useMemo(() => {
    if (!organizationUsers?.length) return [];
    return organizationUsers.map((u: AdditionalUser) => ({
      label: u.name || u.email,
      value: u.email
    }));
  }, [organizationUsers]);

  if (options.length === 0) return null;

  const label = props.label ?? (t("bulkMessagingLog.filters.users") as string);
  const size = props.size ?? "small";

  if (props.multiple === true) {
    const multiProps = props as MultiProps;
    const selected = options.filter((o) => multiProps.value.includes(o.value));
    return (
      <Autocomplete
        multiple
        size={size}
        sx={multiProps.sx}
        fullWidth={multiProps.fullWidth}
        options={options}
        getOptionLabel={(o) => (typeof o === "string" ? o : o.label)}
        value={selected}
        onChange={(_, newValue) => {
          multiProps.onChange((newValue as OrgUserOption[]).map((v) => v.value));
        }}
        renderInput={(params) => <TextField {...params} label={label} />}
      />
    );
  }

  const singleProps = props as SingleProps;
  const selected = options.find((o) => o.value === singleProps.value) || null;
  return (
    <Autocomplete
      size={size}
      sx={singleProps.sx}
      fullWidth={singleProps.fullWidth}
      options={options}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.label)}
      value={selected}
      onChange={(_, newValue) => {
        const v = newValue as OrgUserOption | null;
        singleProps.onChange(v ? v.value : null);
      }}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
}
